import { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useFinancialStore } from "@/store/useFinancialStore";
import MarketChart from "@/components/MarketChart";
import LiveMarketData from "@/components/LiveMarketData";
import GlassCard from "@/components/GlassCard";
import GlassButton from "@/components/GlassButton";
import LiquidGlassContainer from "@/components/LiquidGlassContainer";

export default function TradingPage() {
  const { portfolio, marketData, placeTrade, loading: storeLoading } = useFinancialStore();
  const [searchParams] = useSearchParams();
  const [selectedSymbol, setSelectedSymbol] = useState(searchParams.get("symbol") || "AAPL");
  const [orderType, setOrderType] = useState("MARKET");
  const [side, setSide] = useState(searchParams.get("side")?.toUpperCase() === "SELL" ? "SELL" : "BUY");

  useEffect(() => {
    const symbol = searchParams.get("symbol");
    if (symbol && marketData[symbol]) {
      setSelectedSymbol(symbol);
    }
    const sideParam = searchParams.get("side");
    if (sideParam === "BUY" || sideParam === "SELL") {
      setSide(sideParam);
    }
  }, [searchParams, marketData]);
  const [quantity, setQuantity] = useState("");
  const [price, setPrice] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const marketList = useMemo(() => Object.values(marketData), [marketData]);
  const selectedStock = marketData[selectedSymbol];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quantity || isNaN(parseFloat(quantity))) return;
    
    setIsLoading(true);

    try {
      const success = await placeTrade({
        symbol: selectedSymbol,
        side,
        orderType,
        quantity: parseFloat(quantity),
        price: orderType === "LIMIT" ? parseFloat(price) : undefined,
      });

      if (success) {
        setQuantity("");
        setPrice("");
      } else {
        alert("Execution pipeline busy. Please retry.");
      }
    } catch (error) {
      alert("Terminal protocol error.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary selection:bg-brand-teal/30">
      {/* Header */}
      <header className="bg-bg-secondary/50 backdrop-blur-xl border-b border-border-primary sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-text-secondary hover:text-text-primary transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <h1 className="text-xl tracking-tight font-semibold text-text-primary">
                Trading Terminal
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm font-medium text-text-secondary uppercase tracking-tight">
                Buying Power: <span className="text-brand-teal-light tabular-nums font-bold">${(portfolio?.cashBalance || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Live Market Data */}
        <div className="mb-8 relative z-10">
          <LiveMarketData 
            symbols={["AAPL", "TSLA", "GOOGL", "MSFT", "BTC", "ETH"]}
            refreshInterval={5000}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Real-time Chart */}
          <div className="lg:col-span-2">
            <LiquidGlassContainer variant="flowing" intensity="medium" className="p-6 relative overflow-hidden group min-h-[500px]">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-teal/5 to-brand-plum/5 pointer-events-none" />
              <MarketChart 
                symbol={selectedSymbol}
                height={500}
              />
            </LiquidGlassContainer>
          </div>

          {/* Trading Form */}
          <div className="space-y-6">
            <GlassCard variant="elevated" className="p-6 relative overflow-hidden group">
               <div className="absolute top-0 right-0 w-32 h-32 bg-brand-teal/5 blur-3xl group-hover:bg-brand-teal/10 transition-all duration-700 rounded-full pointer-events-none" />
              <h2 className="text-lg font-medium text-text-primary mb-6">Order Entry</h2>
              
              <form onSubmit={handleSubmit} className="space-y-5 relative z-10">
                {/* Symbol Selection */}
                <div>
                  <label htmlFor="symbol" className="block text-xs font-semibold uppercase tracking-wider text-text-secondary mb-2">
                    Asset
                  </label>
                    <select
                      id="symbol"
                      value={selectedSymbol}
                      onChange={(e) => setSelectedSymbol(e.target.value)}
                      className="w-full bg-bg-secondary/50 border border-border-primary rounded-xl px-4 py-3 text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-teal/50 focus:border-transparent transition-all backdrop-blur-md appearance-none"
                      style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem' }}
                    >
                      {marketList.map((stock) => (
                        <option key={stock.symbol} value={stock.symbol} className="bg-bg-primary text-text-primary">
                          {stock.symbol} - ${stock.price.toFixed(2)}
                        </option>
                      ))}
                    </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Order Type */}
                  <div>
                    <label htmlFor="orderType" className="block text-xs font-semibold uppercase tracking-wider text-text-secondary mb-2">
                      Type
                    </label>
                    <select
                      id="orderType"
                      value={orderType}
                      onChange={(e) => setOrderType(e.target.value)}
                      className="w-full bg-bg-secondary/50 border border-border-primary rounded-xl px-4 py-3 text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-teal/50 focus:border-transparent transition-all backdrop-blur-md appearance-none"
                      style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem' }}
                    >
                      <option value="MARKET" className="bg-bg-primary text-text-primary">Market</option>
                      <option value="LIMIT" className="bg-bg-primary text-text-primary">Limit</option>
                      <option value="STOP" className="bg-bg-primary text-text-primary">Stop</option>
                      <option value="STOP_LIMIT" className="bg-bg-primary text-text-primary">Stop Lmt</option>
                    </select>
                  </div>

                  {/* Side */}
                  <div>
                     <label htmlFor="side" className="block text-xs font-semibold uppercase tracking-wider text-text-secondary mb-2">
                      Side
                    </label>
                     <div className="flex bg-bg-secondary/50 border border-border-primary rounded-xl p-1 backdrop-blur-md">
                        <button
                          type="button"
                          onClick={() => setSide("BUY")}
                          className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${side === "BUY" ? "bg-brand-teal/20 text-brand-teal-light shadow-glow" : "text-text-secondary hover:text-text-primary"}`}
                        >
                          Buy
                        </button>
                        <button
                           type="button"
                          onClick={() => setSide("SELL")}
                          className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${side === "SELL" ? "bg-brand-plum/20 text-brand-plum-light shadow-[0_0_15px_rgba(255,107,107,0.3)]" : "text-text-secondary hover:text-text-primary"}`}
                        >
                          Sell
                        </button>
                     </div>
                  </div>
                </div>

                {/* Quantity */}
                <div>
                  <label htmlFor="quantity" className="block text-xs font-semibold uppercase tracking-wider text-text-secondary mb-2">
                    Size (Qty)
                  </label>
                  <input
                    id="quantity"
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder="0"
                    className="w-full bg-bg-secondary/50 border border-border-primary rounded-xl px-4 py-3 text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-teal/50 focus:border-transparent transition-all backdrop-blur-md tabular-nums font-medium"
                    required
                  />
                </div>

                {/* Price (for Limit orders) */}
                {orderType === "LIMIT" && (
                  <div>
                    <label htmlFor="price" className="block text-xs font-semibold uppercase tracking-wider text-text-secondary mb-2">
                      Limit Price
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted font-medium">$</span>
                      <input
                        id="price"
                        type="number"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        placeholder="0.00"
                        className="w-full bg-bg-secondary/50 border border-border-primary rounded-xl pl-8 pr-4 py-3 text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-teal/50 focus:border-transparent transition-all backdrop-blur-md tabular-nums font-medium"
                        required
                      />
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="pt-2">
                  <GlassButton
                    type="submit"
                    disabled={isLoading || !quantity || (orderType === "LIMIT" && !price)}
                    variant={side === "BUY" ? "success" : "danger"}
                    size="lg"
                    glowEffect
                    className="w-full font-bold tracking-wide"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                         Processing...
                      </span>
                    ) : `${side} ${quantity || '0'} ${selectedSymbol}`}
                  </GlassButton>
                </div>
              </form>
            </GlassCard>

            {/* Order Summary */}
            {selectedStock && (
              <GlassCard variant="glow" className="p-6">
                <h3 className="text-sm tracking-wide uppercase font-semibold text-text-secondary mb-4">Market Quote</h3>
                <div className="space-y-3 text-sm font-medium">
                  <div className="flex justify-between items-center py-1">
                    <span className="text-text-secondary">Instrument</span>
                    <span className="text-text-primary font-semibold flex items-center gap-2">
                       <div className="w-2 h-2 rounded-full bg-brand-teal shadow-glow"></div>
                       {selectedStock.symbol}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-text-secondary">Ask Price</span>
                    <span className="text-text-primary tabular-nums">${selectedStock.price.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-text-secondary">24h Delta</span>
                    <span className={`tabular-nums font-semibold ${selectedStock.change >= 0 ? 'text-brand-teal-light' : 'text-brand-plum-light'}`}>
                      {selectedStock.change >= 0 ? '↗ +' : '↘ '}{selectedStock.change.toFixed(2)} ({selectedStock.changePercent.toFixed(2)}%)
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-text-secondary">Notional Cost</span>
                    <span className="text-text-primary tabular-nums font-bold">
                      ${quantity ? (parseFloat(quantity) * (orderType === 'LIMIT' && price ? parseFloat(price) : selectedStock.price)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '0.00'}
                    </span>
                  </div>
                </div>
              </GlassCard>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 